import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const G      = '#004D35';
const DARK   = '#0a1a0f';
const BORDER = '#e8e5e0';
const BG     = '#f5f3ef';
const MINT   = '#a8f0c6';

const STATUS_META = {
  new:             { label: 'New',             color: '#0D6B4B', bg: '#e8f5ef' },
  booking_started: { label: 'Booking started', color: '#7c5c00', bg: '#fff8e1' },
  booked:          { label: 'Booked',          color: '#1565c0', bg: '#e3f2fd' },
  contacted:       { label: 'Contacted',       color: '#e65100', bg: '#fff3e0' },
  won:             { label: 'Won',             color: '#004D35', bg: '#a8f0c6' },
  lost:            { label: 'Lost',            color: '#888',    bg: '#f0ede8' },
  met:             { label: 'Met',             color: '#6a1b9a', bg: '#f3e5f5' },
  abandoned:       { label: 'Abandoned',       color: '#999',    bg: '#f5f5f5' },
};

const FILTER_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'new',       label: 'New' },
  { key: 'booked',    label: 'Booked' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'won',       label: 'Won' },
  { key: 'lost',      label: 'Lost' },
];

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#888', bg: '#f0ede8' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
      color: m.color, background: m.bg,
    }}>
      {m.label}
    </span>
  );
}

function SourceBadge({ source }) {
  const isBooking = source === 'booking';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
      color: isBooking ? '#1565c0' : '#7c5c00',
      background: isBooking ? '#e3f2fd' : '#fff8e1',
    }}>
      {isBooking ? 'Booked call' : 'Message'}
    </span>
  );
}

function truncate(str, n = 120) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ── LeadCard ─────────────────────────────────────────────────────────────────
function LeadCard({ lead, noteValue, onNoteChange, onSaveNote, onStatusChange, saving, noteSaved, expanded, onToggleExpand, isMobile }) {
  const sysStatus = !['contacted', 'won', 'lost'].includes(lead.status);

  return (
    <div style={{
      background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16,
      padding: isMobile ? '16px' : '20px 24px',
      transition: 'box-shadow 0.15s',
    }}>
      {/* ── TOP ROW ── */}
      <div style={{
        display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: DARK }}>
            {lead.business_owner_name || '(no name)'}
          </span>
          <a
            href={`mailto:${lead.business_owner_email}`}
            style={{ fontSize: 13, color: G, textDecoration: 'none', fontWeight: 500 }}
          >
            {lead.business_owner_email}
          </a>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <SourceBadge source={lead.source} />
          <StatusBadge status={lead.status} />
          <span style={{ fontSize: 12, color: '#aaa' }}>{fmtDate(lead.created_at)}</span>
        </div>
      </div>

      {/* ── MESSAGE PREVIEW ── */}
      {lead.message && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 13, color: '#555', lineHeight: 1.6,
            background: '#f9f8f6', borderRadius: 8, padding: '10px 14px',
          }}>
            {expanded ? lead.message : truncate(lead.message)}
            {lead.message.length > 120 && (
              <button
                onClick={onToggleExpand}
                style={{ background: 'none', border: 'none', color: G, fontSize: 12, fontWeight: 600, cursor: 'pointer', paddingLeft: 6 }}
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── MEETING TIME ── */}
      {lead.meeting_start_at && (
        <div style={{ marginBottom: 12, fontSize: 13, color: '#1565c0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>📅</span>
          <span>Meeting: {fmtDateTime(lead.meeting_start_at)}</span>
        </div>
      )}

      {/* ── ACTIONS ROW ── */}
      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start',
        borderTop: `1px solid ${BORDER}`, paddingTop: 12, marginTop: 4,
      }}>

        {/* Status dropdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 150 }}>
          <label style={{ fontSize: 11, color: '#aaa', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Status
          </label>
          <div style={{ position: 'relative' }}>
            <select
              value={lead.status}
              onChange={e => onStatusChange(e.target.value)}
              disabled={saving}
              style={{
                width: '100%', border: `1.5px solid ${BORDER}`, borderRadius: 8,
                padding: '7px 28px 7px 10px', fontSize: 13, fontWeight: 600, color: DARK,
                background: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                appearance: 'none', WebkitAppearance: 'none',
              }}
            >
              {/* If current status is system-managed, show it as a disabled option */}
              {sysStatus && (
                <option value={lead.status} disabled>
                  {STATUS_META[lead.status]?.label || lead.status}
                </option>
              )}
              <option value="contacted">Contacted</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#666', fontSize: 10 }}>▼</span>
          </div>
        </div>

        {/* Private notes */}
        <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, color: '#aaa', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Private notes
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <textarea
              value={noteValue}
              onChange={e => onNoteChange(e.target.value)}
              placeholder="Add private notes…"
              rows={2}
              style={{
                flex: 1, border: `1.5px solid ${BORDER}`, borderRadius: 8,
                padding: '7px 10px', fontSize: 13, color: DARK,
                resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
                outline: 'none',
              }}
            />
            <button
              onClick={onSaveNote}
              disabled={saving}
              style={{
                padding: '7px 14px', borderRadius: 8, border: 'none',
                cursor: saving ? 'not-allowed' : 'pointer',
                background: noteSaved ? MINT : G,
                color: noteSaved ? G : '#fff',
                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                transition: 'background 0.2s', flexShrink: 0,
              }}
            >
              {noteSaved ? 'Saved ✓' : saving ? '…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AdvisorLeads page ─────────────────────────────────────────────────────────
export default function AdvisorLeads() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [leads,        setLeads]        = useState([]);
  const [counts,       setCounts]       = useState({});
  const [loading,      setLoading]      = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedMsg,  setExpandedMsg]  = useState(null);
  const [savingId,     setSavingId]     = useState(null);
  const [notes,        setNotes]        = useState({});
  const [savedNotes,   setSavedNotes]   = useState({});
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const fetchLeads = useCallback(async (status) => {
    setLoading(true);
    try {
      const qs  = status && status !== 'all' ? `?status=${status}` : '';
      const res = await fetch(getApiUrl(`/api/leads${qs}`), {
        credentials: 'include',
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
        setCounts(data.counts || {});
        // seed notes state from fresh data, but don't overwrite unsaved local edits
        setNotes(prev => {
          const next = { ...prev };
          (data.leads || []).forEach(l => {
            if (!(l.id in next)) next[l.id] = l.notes || '';
          });
          return next;
        });
      }
    } catch (e) {
      console.error('fetchLeads error:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchLeads(filterStatus); }, [filterStatus, fetchLeads]);

  const patchLead = useCallback(async (id, patch) => {
    setSavingId(id);
    try {
      const res = await fetch(getApiUrl(`/api/leads/${id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const { lead } = await res.json();
        setLeads(prev => prev.map(l => l.id === id ? { ...l, ...lead } : l));
        if ('notes' in patch) {
          setSavedNotes(prev => ({ ...prev, [id]: true }));
          setTimeout(() => setSavedNotes(prev => ({ ...prev, [id]: false })), 2200);
        }
      }
    } catch (e) {
      console.error('patchLead error:', e);
    } finally {
      setSavingId(null);
    }
  }, [getAuthHeader]);

  const totalAll = Object.values(counts).reduce((s, n) => s + (parseInt(n) || 0), 0);

  const tabCount = (key) => {
    if (key === 'all') return totalAll;
    return parseInt(counts[key] || 0);
  };

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── HEADER ── */}
      <div style={{
        background: '#fff', borderBottom: `1px solid ${BORDER}`,
        padding: isMobile ? '14px 16px' : '18px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/advisor/dashboard')}
            style={{ background: 'none', border: 'none', color: G, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            ← Dashboard
          </button>
          <div style={{ width: 1, height: 16, background: BORDER }} />
          <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif' }}>
            My Leads
          </h1>
        </div>
        <span style={{ fontSize: 13, color: '#aaa' }}>
          {totalAll} total lead{totalAll !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '16px' : '28px 32px' }}>

        {/* ── FILTER TABS ── */}
        <div style={{
          display: 'flex', gap: 0, borderBottom: `1px solid ${BORDER}`,
          marginBottom: 24, overflowX: 'auto',
        }}>
          {FILTER_TABS.map(({ key, label }) => {
            const count  = tabCount(key);
            const active = filterStatus === key;
            return (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                style={{
                  padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: active ? `2px solid ${G}` : '2px solid transparent',
                  color: active ? G : '#888',
                  fontWeight: active ? 700 : 500, fontSize: 13,
                  whiteSpace: 'nowrap', marginBottom: -1,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {label}
                {count > 0 && (
                  <span style={{
                    background: active ? G : '#e8e5e0',
                    color: active ? '#fff' : '#777',
                    fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 999,
                  }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── LEAD LIST ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: '#aaa', fontSize: 15 }}>
            Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', marginBottom: 8 }}>
              No leads{filterStatus !== 'all' ? ` with status "${filterStatus}"` : ' yet'}
            </div>
            <div style={{ fontSize: 14, color: '#aaa' }}>
              {filterStatus !== 'all'
                ? 'Try "All" to see every lead.'
                : 'When a business owner contacts you, they\'ll appear here.'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {leads.map(lead => (
              <LeadCard
                key={lead.id}
                lead={lead}
                noteValue={notes[lead.id] ?? (lead.notes || '')}
                onNoteChange={val  => setNotes(prev => ({ ...prev, [lead.id]: val }))}
                onSaveNote={()     => patchLead(lead.id, { notes: notes[lead.id] ?? '' })}
                onStatusChange={s  => patchLead(lead.id, { status: s })}
                saving={savingId === lead.id}
                noteSaved={!!savedNotes[lead.id]}
                expanded={expandedMsg === lead.id}
                onToggleExpand={() => setExpandedMsg(prev => prev === lead.id ? null : lead.id)}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
